import type { SessionTokens } from '@occ/contracts';

/**
 * Almacenamiento de la sesión del operador.
 *
 * Los tokens viven en `localStorage` y no en una cookie porque el panel puede
 * servirse desde un dominio distinto al del backend (white-label, RF-21) y una
 * cookie `SameSite` no sobreviviría a ese cruce. El precio es exposición a XSS,
 * que se mitiga con el acceso corto (15 min) y el refresh rotatorio del M9.
 */

const SESSION_KEY = 'occ.session';

export type StoredSession = {
  accessToken: string;
  refreshToken: string;
  /** Epoch en milisegundos. Calculado al guardar, no confiado al reloj del servidor. */
  expiresAt: number;
};

/** Margen para renovar antes de que el token caduque en pleno vuelo de una petición. */
const EXPIRY_SKEW_MS = 30_000;

let cached: StoredSession | null | undefined;

export const loadSession = (): StoredSession | null => {
  if (cached !== undefined) return cached;
  try {
    const raw = window.localStorage.getItem(SESSION_KEY);
    if (!raw) {
      cached = null;
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as StoredSession).accessToken === 'string' &&
      typeof (parsed as StoredSession).refreshToken === 'string' &&
      typeof (parsed as StoredSession).expiresAt === 'number'
    ) {
      cached = parsed as StoredSession;
      return cached;
    }
  } catch {
    // Almacenamiento corrupto o bloqueado: se trata como "sin sesión".
  }
  cached = null;
  return null;
};

export const saveSession = (tokens: SessionTokens): StoredSession => {
  const session: StoredSession = {
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
    expiresAt: Date.now() + tokens.expiresInSeconds * 1000,
  };
  cached = session;
  try {
    window.localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // Sin persistencia la sesión dura lo que la pestaña; sigue siendo usable.
  }
  return session;
};

export const clearSession = (): void => {
  cached = null;
  try {
    window.localStorage.removeItem(SESSION_KEY);
  } catch {
    // Nada que limpiar.
  }
};

export const isExpired = (session: StoredSession): boolean =>
  session.expiresAt - EXPIRY_SKEW_MS <= Date.now();

/**
 * Token de MFA: credencial de un solo uso y vida corta. Se queda en memoria
 * porque persistirla convertiría un segundo factor en un factor guardado.
 */
let pendingMfaToken: string | null = null;

export const setPendingMfaToken = (token: string | null): void => {
  pendingMfaToken = token;
};

export const getPendingMfaToken = (): string | null => pendingMfaToken;
